import type { ParticipantSortKey, TaskSortKey } from '../../domain/campaignActivity/sorting';
import React, { useRef, useState } from 'react';
import { PortalMenu } from '../../components/PortalMenu';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react';
import { Tabs } from '../../components/Tabs';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import { paths } from '../../routes/paths';
import { buildBrowseTasksSearchParams } from '../BrowseTasksPage/filter';
import type { TaskStatus, User } from '../../types';
import { useAdminCampaignActivityViewModel } from './activityViewModel';

const cell = 'px-4 py-3 text-left';
const taskRowBackground: Record<TaskStatus, string> = {
  available: 'bg-white',
  claimed: 'bg-amber-50/60',
  in_progress: 'bg-violet-50/60',
  in_review: 'bg-blue-50/60',
  changes_requested: 'bg-rose-50/60',
  approved: 'bg-emerald-50/60',
  archived: 'bg-gray-100/60',
};
const button =
  'rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 hover:bg-gray-50';
function duration(ms: number | null) {
  if (ms === null) return '—';
  const minutes = Math.floor(ms / 60000);
  if (!minutes) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h`;
}
function hoursAndMinutes(ms: number | null) {
  if (ms === null) return '—';
  const minutes = Math.floor(ms / 60000);
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
function TaskLink({ id }: { id: string }) {
  return (
    <Link
      className="text-primary hover:underline"
      to={paths.browseTasks(buildBrowseTasksSearchParams({ taskId: id, autorun: true }))}
    >
      {id}
    </Link>
  );
}

export function ActivityPanel({
  campaignId,
  campaignName,
  users,
  writerIds,
}: {
  campaignId: string;
  campaignName: string;
  users: User[];
  writerIds?: string[];
}) {
  const vm = useAdminCampaignActivityViewModel(campaignId, campaignName, users, writerIds);
  const reviewing = vm.mode === 'reviewers';
  return (
    <section aria-label="Activity" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Activity</h2>
        <div className="flex gap-2">
          <button className={button} disabled={vm.busy} onClick={vm.exportCsv}>
            Export CSV
          </button>
          <button
            className={button}
            disabled={vm.busy || vm.pending}
            onClick={() => void vm.refresh()}
          >
            <RefreshCw className="mr-2 inline h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>
      <Tabs
        tabs={[
          { id: 'writers', label: 'Writers' },
          { id: 'reviewers', label: 'Reviewers' },
          { id: 'eventLog', label: 'Event log' },
        ]}
        activeId={vm.mode}
        onChange={(id) => vm.setMode(id as typeof vm.mode)}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          aria-label={vm.mode === 'eventLog' ? 'Search events' : 'Search people'}
          placeholder={
            vm.mode === 'eventLog' ? 'Search events or task ID' : 'Search name, email or expert ID'
          }
          value={vm.searchQuery}
          onChange={(e) => vm.setSearchQuery(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm sm:w-80"
        />
        {vm.mode !== 'eventLog' && (
          <div
            role="group"
            aria-label="Assignment history"
            className="inline-flex rounded-md border border-gray-300 p-1"
          >
            {[true, false].map((current) => (
              <button
                key={String(current)}
                aria-pressed={vm.current === current}
                className={`rounded px-4 py-1.5 text-sm ${vm.current === current ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                onClick={() => vm.setCurrent(current)}
              >
                {current ? 'Current' : 'All Time'}
              </button>
            ))}
          </div>
        )}
      </div>
      {vm.error && (
        <p role="alert" className="text-sm text-red-700">
          {vm.error}
        </p>
      )}
      {vm.notice && (
        <p role="status" className="text-sm text-gray-700">
          {vm.notice}
        </p>
      )}
      {vm.busy ? (
        <p role="status" className="py-8 text-center text-gray-500">
          Loading activity…
        </p>
      ) : vm.mode === 'eventLog' ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Event', 'Actor', 'Task ID', 'Transition'].map((label) => (
                    <th key={label} className={cell}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vm.visibleEvents.map((event) => (
                  <tr key={event.id} className="border-t border-gray-100">
                    <td className={cell}>{event.createdAt.toLocaleString()}</td>
                    <td className={cell}>{event.kind.replaceAll('_', ' ')}</td>
                    <td className={cell}>{event.actorName || event.actorId}</td>
                    <td className={cell}>
                      <TaskLink id={event.taskId} />
                    </td>
                    <td className={cell}>
                      {event.fromStatus ?? '—'} → {event.toStatus ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!vm.eventCount && (
              <p className="p-8 text-center text-gray-500">No events match your search.</p>
            )}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>
              {vm.eventCount} events · Page {vm.page + 1} of {vm.pageCount}
            </span>
            <div className="flex gap-2">
              <button
                className={button}
                disabled={vm.page === 0}
                onClick={() => vm.setPage(vm.page - 1)}
              >
                Previous
              </button>
              <button
                className={button}
                disabled={vm.page + 1 >= vm.pageCount}
                onClick={() => vm.setPage(vm.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {(
                    [
                      ['name', reviewing ? 'Reviewer' : 'Writer'],
                      ['signedOff', 'Signed off tasks'],
                      ['inReview', 'In-review tasks'],
                      [
                        reviewing ? 'averageReviewMs' : 'averageWritingMs',
                        reviewing ? 'Average review time' : 'Average writing time',
                      ],
                      ...(!reviewing ? [['revisionRatio', 'Revision ratio']] : []),
                    ] as [ParticipantSortKey, string][]
                  ).map(([key, label]) => (
                    <SortHeader
                      key={key}
                      label={label}
                      direction={vm.sort.key === key ? vm.sort.direction : undefined}
                      onSort={() => vm.setSort(key)}
                    />
                  ))}
                  {reviewing ? (
                    <th className={cell}>
                      <span className="sr-only">Actions</span>
                    </th>
                  ) : (
                    <SortHeader
                      label="Trusted"
                      direction={vm.sort.key === 'trusted' ? vm.sort.direction : undefined}
                      onSort={() => vm.setSort('trusted')}
                    />
                  )}
                </tr>
              </thead>
              <tbody>
                {vm.visibleRows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`border-t border-gray-200 transition-colors duration-700 ease-out motion-reduce:transition-none ${
                        !reviewing && vm.isTrusted(row.id)
                          ? vm.trustedShineId === row.id
                            ? 'bg-transparent motion-reduce:bg-amber-50'
                            : 'bg-amber-50'
                          : 'bg-transparent'
                      } ${!reviewing && vm.trustedShineId === row.id ? 'trusted-writer-shine' : ''}`}
                      onAnimationEnd={(event) => {
                        if (event.target === event.currentTarget && vm.trustedShineId === row.id) {
                          vm.clearTrustedShine();
                        }
                      }}
                    >
                      <td className={cell}>
                        <button
                          aria-expanded={vm.expanded.has(row.id)}
                          aria-controls={`tasks-${row.id}`}
                          onClick={() => vm.toggleExpanded(row.id)}
                          className="flex items-center gap-2 text-left"
                        >
                          {vm.expanded.has(row.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span>
                            <span className="font-medium">{row.name}</span>
                            <span className="block text-xs text-gray-500">{row.email}</span>
                            <span className="block text-xs text-gray-500">{row.expertId}</span>
                          </span>
                        </button>
                      </td>
                      <td className={cell}>{row.signedOff}</td>
                      <td className={cell}>{row.inReview}</td>
                      <td className={cell}>
                        {duration(reviewing ? row.averageReviewMs : row.averageWritingMs)}
                      </td>
                      {!reviewing && (
                        <td className={cell} title="Number of revisions divided by number of tasks">
                          {row.revisionRatio === null ? '—' : row.revisionRatio.toFixed(2)}
                        </td>
                      )}
                      {!reviewing && (
                        <td className={cell}>
                          <button type="button" disabled={vm.pending || !vm.canSetTrusted(row.id)}
                            aria-label={`${vm.isTrusted(row.id) ? 'Remove trusted status from' : 'Mark as trusted:'} ${row.name}`}
                            aria-pressed={vm.isTrusted(row.id)}
                            onClick={() => void vm.toggleTrusted(row.id)}
                            className="rounded border px-2 py-1 text-xs disabled:opacity-50">
                            {vm.isTrusted(row.id) ? 'Trusted' : 'Mark Trusted'}
                          </button>
                        </td>
                      )}
                      {reviewing && (
                        <td className={cell}>
                          <ReviewerActions
                            name={row.name}
                            count={vm.assignedTasks(row.id).length}
                            pending={vm.pending}
                            onRelease={() => void vm.bulkUpdate(row.id)}
                            onReassign={() => vm.openReassign(row.id)}
                          />
                        </td>
                      )}
                    </tr>
                    {vm.expanded.has(row.id) && (
                      <tr id={`tasks-${row.id}`}>
                        <td colSpan={reviewing ? 5 : 6} className="bg-white p-4">
                          <table className="w-full text-sm">
                            <thead>
                              <tr>
                                {(
                                  [
                                    ['id', 'Task ID'],
                                    ['writer', 'Writer'],
                                    ['status', 'Current state'],
                                    ['reviewMs', 'Time in review'],
                                    [
                                      reviewing ? 'sinceLastActivityMs' : 'progressMs',
                                      reviewing ? 'Time since last activity' : 'Time in progress',
                                    ],
                                  ] as [TaskSortKey, string][]
                                ).map(([key, label]) => (
                                  <SortHeader
                                    key={key}
                                    label={label}
                                    direction={
                                      vm.taskSort.key === key ? vm.taskSort.direction : undefined
                                    }
                                    onSort={() => vm.setTaskSort(key)}
                                  />
                                ))}
                                {reviewing && (
                                  <th scope="col" className={cell}>
                                    Actions
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {row.tasks.map(
                                ({ task, reviewMs, progressMs, sinceLastActivityMs }) => (
                                  <tr
                                    key={task.id}
                                    className={`border-t border-gray-200 ${taskRowBackground[task.status]}`}
                                  >
                                    <td className={cell}>
                                      <TaskLink id={task.id} />
                                    </td>
                                    <td className={cell}>
                                      {task.ownerName ||
                                        task.ownerEmail ||
                                        task.ownerId ||
                                        'Unassigned'}
                                    </td>
                                    <td className={cell}>{task.status.replaceAll('_', ' ')}</td>
                                    <td className={cell}>{duration(reviewMs)}</td>
                                    <td className={cell}>
                                      {reviewing
                                        ? hoursAndMinutes(sinceLastActivityMs)
                                        : duration(progressMs)}
                                    </td>
                                    {reviewing && (
                                      <td className={cell}>
                                        <div className="flex gap-2">
                                          <button
                                            type="button"
                                            className={button}
                                            disabled={
                                              vm.pending ||
                                              task.status !== 'in_review' ||
                                              task.reviewerId !== row.id
                                            }
                                            onClick={() => vm.openReassign(row.id, task)}
                                          >
                                            Reassign
                                          </button>
                                          <button
                                            type="button"
                                            className={button}
                                            disabled={
                                              vm.pending ||
                                              task.status !== 'in_review' ||
                                              task.reviewerId !== row.id
                                            }
                                            onClick={() =>
                                              void vm.bulkUpdate(row.id, undefined, task)
                                            }
                                          >
                                            Release
                                          </button>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                          {!row.tasks.length && (
                            <p className="p-4 text-gray-500">No tasks in this view.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {!vm.visibleRows.length && (
              <p className="p-8 text-center text-gray-500">No people match your search.</p>
            )}
          </div>
        </>
      )}
      {vm.reassignId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !vm.pending) vm.setReassignId(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reassign-title"
            className="w-full max-w-lg space-y-4 rounded-lg bg-white p-6 shadow-xl"
          >
            <h3 id="reassign-title" className="text-lg font-semibold">
              {vm.reassignTask
                ? `Reassign task ${vm.reassignTask.id}`
                : `Reassign ${vm.assignedTasks(vm.reassignId).length} in-review tasks`}
            </h3>
            <p className="text-sm text-gray-600">
              Choose a reviewer or admin for {vm.reassignTask ? 'this task' : 'these tasks'} in{' '}
              {campaignName}.
            </p>
            <SearchableDropdown<string>
              id="activity-reviewer"
              value={vm.targetId}
              onChange={(value) => vm.setTargetId(value ?? '')}
              options={vm.reviewers
                .filter((u) => u.uid !== vm.reassignId)
                .map((u) => ({
                  value: u.uid,
                  label: u.displayName || u.email || u.uid,
                  description: u.email || undefined,
                }))}
              placeholder="Choose a reviewer or admin"
              searchPlaceholder="Search reviewers and admins"
              disabled={vm.pending}
            />
            <div className="flex justify-end gap-2">
              <button
                autoFocus
                className={button}
                disabled={vm.pending}
                onClick={() => vm.setReassignId(null)}
              >
                Cancel
              </button>
              <button
                className={button}
                disabled={vm.pending || !vm.targetId}
                onClick={() =>
                  void vm.bulkUpdate(vm.reassignId!, vm.targetId, vm.reassignTask ?? undefined)
                }
              >
                {vm.pending ? 'Reassigning…' : vm.reassignTask ? 'Reassign task' : 'Reassign tasks'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ReviewerActions({
  name,
  count,
  pending,
  onRelease,
  onReassign,
}: {
  name: string;
  count: number;
  pending: boolean;
  onRelease: () => void;
  onReassign: () => void;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        ref={anchor}
        aria-label={`Actions for ${name}`}
        aria-expanded={open}
        aria-haspopup="menu"
        className="rounded p-2 hover:bg-gray-100"
        onClick={() => setOpen(!open)}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <PortalMenu
          anchorRef={anchor}
          onRequestClose={() => setOpen(false)}
          role="menu"
          ariaLabel={`Tasks assigned to ${name}`}
          className="w-60 p-1"
        >
          <button
            role="menuitem"
            className="block w-full p-2 text-left hover:bg-gray-50 disabled:opacity-50"
            disabled={pending || !count}
            onClick={() => {
              setOpen(false);
              onRelease();
            }}
          >
            Release {count} in-review tasks
          </button>
          <button
            role="menuitem"
            className="block w-full p-2 text-left hover:bg-gray-50 disabled:opacity-50"
            disabled={pending || !count}
            onClick={() => {
              setOpen(false);
              onReassign();
            }}
          >
            Reassign in-review tasks…
          </button>
        </PortalMenu>
      )}
    </>
  );
}

function SortHeader({
  label,
  direction,
  onSort,
}: {
  label: string;
  direction?: 'ascending' | 'descending';
  onSort: () => void;
}) {
  const Icon =
    direction === 'ascending' ? ArrowUp : direction === 'descending' ? ArrowDown : ArrowUpDown;
  return (
    <th scope="col" aria-sort={direction ?? 'none'} className={cell}>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        onClick={onSort}
      >
        {label}
        <Icon
          aria-hidden="true"
          className={`h-3.5 w-3.5 ${direction ? 'text-primary' : 'text-gray-400'}`}
        />
      </button>
    </th>
  );
}
